/**
 * Represents a segment of a hint string, where some segments may be
 * highlighted (wrapped in **double asterisks** in the source text).
 */
export interface HintSegment {
  text: string;
  highlighted: boolean;
}

/**
 * Parses a hint string containing **highlighted** markers into segments.
 *
 * Used in Fill in the Blank exercises where the native-language hint shows
 * the complete sentence with the answer word wrapped in `**...**`.
 *
 * Falls back gracefully for old-format hints (no markers) — returns a single
 * unhighlighted segment containing the full text.
 *
 * @example
 * parseHighlightedHint("Hij heeft **koorts** sinds gisteren.")
 * // => [
 * //   { text: "Hij heeft ", highlighted: false },
 * //   { text: "koorts", highlighted: true },
 * //   { text: " sinds gisteren.", highlighted: false },
 * // ]
 */
export function parseHighlightedHint(hint: string): HintSegment[] {
  // Match standard ASCII ** and common Unicode variants (fullwidth ＊, heavy ✱, etc.)
  const pattern = /(?:\*\*|＊＊|✱✱)(.*?)(?:\*\*|＊＊|✱✱)/g;
  const segments: HintSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(hint)) !== null) {
    // Add plain text before the match (if any)
    if (match.index > lastIndex) {
      segments.push({ text: hint.slice(lastIndex, match.index), highlighted: false });
    }

    // Add the highlighted word(s)
    if (match[1].length > 0) {
      segments.push({ text: match[1], highlighted: true });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining plain text after the last match
  if (lastIndex < hint.length) {
    segments.push({ text: hint.slice(lastIndex), highlighted: false });
  }

  // If no segments were produced (empty string), return a single empty segment
  if (segments.length === 0) {
    segments.push({ text: hint, highlighted: false });
  }

  // Ensure spacing around highlighted segments.
  // AI-generated hints sometimes omit spaces around ** markers, e.g.
  // "We need to**meet the deadline**by Friday" — fix that defensively.
  return ensureHighlightSpacing(segments);
}

/**
 * Post-processes parsed segments to guarantee a space exists between
 * highlighted and non-highlighted segments. Punctuation that normally
 * attaches to the preceding word (.,;:!?) is kept adjacent.
 */
function ensureHighlightSpacing(segments: HintSegment[]): HintSegment[] {
  for (let i = 0; i < segments.length; i++) {
    if (!segments[i].highlighted) continue;

    // Check preceding segment — ensure it ends with a space
    if (i > 0) {
      const prev = segments[i - 1];
      if (!prev.highlighted && prev.text.length > 0 && !prev.text.endsWith(' ')) {
        segments[i - 1] = { text: prev.text + ' ', highlighted: false };
      }
    }

    // Check following segment — ensure it starts with a space (unless punctuation)
    if (i < segments.length - 1) {
      const next = segments[i + 1];
      if (
        !next.highlighted &&
        next.text.length > 0 &&
        !next.text.startsWith(' ') &&
        !/^[.,;:!?)\]}'"]/.test(next.text)
      ) {
        segments[i + 1] = { text: ' ' + next.text, highlighted: false };
      }
    }
  }

  return segments;
}
